import { useEffect, useRef, useState } from 'react';
import {
  ChevronRight, Copy, File as FileIcon, FileText, Folder, FolderPlus, FolderUp, Image as ImageIcon, Loader2, Pencil, Share2, Trash2, Upload, UploadCloud, X,
} from 'lucide-react';
import { spaceErrorMessage, spacesApi, type SpaceCrumb, type SpaceNodeView, type SpaceSummary } from '../../lib/api';
import { MAX_FILES_PER_UPLOAD, readDrop, filesFromInput, type DroppedFile } from '../../lib/space-upload';
import {
  INDEX_STATUS_LABEL,
  SPACE_NODE_DRAG_TYPE,
  accessOf,
  formatBytes,
  isFileDrag,
  isNodeDrag,
  previewKindOf,
  useRefreshWhileIndexing,
  type FolderListingState,
} from '../../lib/spaces';
import { cn, formatDate, timeAgo } from '../../lib/utils';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { RowMenu } from '../ui/row-menu';
import { NameDialog } from './NameDialog';
import { useMoveTarget } from './SpaceNav';

/** What the page needs to hear about, so it can refresh the listing, the tree and the quota. */
export interface FolderViewHandlers {
  onOpenFolder: (folderId: string) => void;
  onOpenFile: (file: SpaceNodeView) => void;
  onShare: (folder: { id: string; address: string }) => void;
  onDownload: (file: SpaceNodeView) => void;
  onCopyPath: (address: string) => void;
  onUpload: (files: DroppedFile[]) => void;
  /** A move by drag, shared with the tree so either can be the target. */
  onMove: (targetFolderId: string) => void;
  /** Something in these folders changed: their listings, and the tree's view of them, are stale. */
  onChanged: (folderIds: Array<string | null | undefined>) => void;
  onError: (message: string) => void;
}

export function NodeIcon({ node }: { node: SpaceNodeView }) {
  if (node.type === 'folder') return <Folder size={14} className="text-orange-500 flex-shrink-0" />;
  const kind = previewKindOf(node.mimeType, node.name);
  const Icon = kind === 'image' ? ImageIcon : kind === 'none' ? FileIcon : FileText;
  return <Icon size={14} className="text-ink-faint flex-shrink-0" />;
}

function crumbLabel(crumb: SpaceCrumb, index: number, space: { handle: string; owner: boolean }): string {
  if (index > 0) return crumb.name;
  // Your own root has no name of its own worth showing; a grantee's view starts at the folder they were
  // given, and the handle says whose it is without saying anything about the folders above it.
  return space.owner ? 'My space' : `@${space.handle} / ${crumb.name}`;
}

function Crumb({
  crumb, label, last, dragging, onOpen, onMove,
}: {
  crumb: SpaceCrumb;
  label: string;
  last: boolean;
  dragging: SpaceNodeView | null;
  onOpen: () => void;
  onMove: (folderId: string) => void;
}) {
  const target = useMoveTarget(crumb.id, last ? null : dragging, onMove);
  return (
    <button
      type="button"
      {...target.props}
      onClick={onOpen}
      disabled={last}
      className={cn(
        'px-1.5 py-0.5 rounded text-xs truncate max-w-[16rem] transition-colors',
        last ? 'text-ink font-semibold cursor-default' : 'text-ink-muted hover:text-ink hover:bg-surface-muted',
        target.over && 'ring-2 ring-orange-400 bg-orange-50 dark:bg-orange-950/40',
      )}
      title={crumb.address}
    >
      {label}
    </button>
  );
}

function ListingRow({
  node, canEdit, isOwner, myEmail, dragging, setDragging, handlers, onRename, onTrash,
}: {
  node: SpaceNodeView;
  canEdit: boolean;
  isOwner: boolean;
  myEmail: string;
  dragging: SpaceNodeView | null;
  setDragging: (node: SpaceNodeView | null) => void;
  handlers: FolderViewHandlers;
  onRename: (node: SpaceNodeView) => void;
  onTrash: (node: SpaceNodeView) => void;
}) {
  const folder = node.type === 'folder';
  const target = useMoveTarget(node.id, folder ? dragging : null, handlers.onMove);
  const open = () => (folder ? handlers.onOpenFolder(node.id) : handlers.onOpenFile(node));
  const indexLabel = node.indexStatus && node.indexStatus !== 'NONE' ? INDEX_STATUS_LABEL[node.indexStatus] : null;

  const menu: Array<{ label: string; onClick: () => void; separated?: boolean }> = [
    { label: folder ? 'Open' : 'Preview', onClick: open },
  ];
  if (!folder) menu.push({ label: 'Download', onClick: () => handlers.onDownload(node) });
  // Any folder below your own root is yours to share; a child is never a root.
  if (folder && isOwner) menu.push({ label: 'Share…', onClick: () => handlers.onShare({ id: node.id, address: node.address }) });
  menu.push({ label: 'Copy MCP path', onClick: () => handlers.onCopyPath(node.address) });
  if (canEdit) {
    menu.push({ label: 'Rename…', onClick: () => onRename(node), separated: true });
    menu.push({ label: 'Move to trash', onClick: () => onTrash(node) });
  }

  return (
    <tr
      {...(folder ? target.props : {})}
      draggable={canEdit}
      onDragStart={(e) => {
        e.dataTransfer.setData(SPACE_NODE_DRAG_TYPE, node.id);
        e.dataTransfer.effectAllowed = 'move';
        setDragging(node);
      }}
      onDragEnd={() => setDragging(null)}
      onClick={open}
      className={cn(
        'group/row border-b border-border-subtle last:border-0 cursor-pointer transition-colors',
        target.over ? 'bg-orange-50 dark:bg-orange-950/40 outline outline-2 outline-orange-400 -outline-offset-2' : 'hover:bg-surface-subtle',
        dragging?.id === node.id && 'opacity-50',
      )}
    >
      <td className="pl-4 pr-2 py-2">
        <span className="flex items-center gap-2 min-w-0">
          <NodeIcon node={node} />
          <span className="text-sm text-ink truncate" title={node.name}>{node.name}</span>
          {node.shared && <Badge variant="info" className="flex-shrink-0 px-1.5 py-0 text-[10px]">Shared</Badge>}
          {node.status === 'PENDING' && (
            <Badge variant="muted" className="flex-shrink-0 px-1.5 py-0 text-[10px]" title="The upload has not been confirmed yet">upload pending</Badge>
          )}
          {indexLabel && <Badge variant="muted" className="flex-shrink-0 px-1.5 py-0 text-[10px]">{indexLabel}</Badge>}
        </span>
      </td>
      <td className="px-2 py-2 text-xs text-ink-muted tabular-nums whitespace-nowrap">{folder ? '—' : formatBytes(node.sizeBytes)}</td>
      <td className="px-2 py-2 text-xs text-ink-muted whitespace-nowrap" title={formatDate(node.updatedAt)}>{timeAgo(node.updatedAt)}</td>
      <td className="px-2 py-2 text-xs text-ink-muted truncate max-w-[14rem]" title={node.modifiedBy ?? undefined}>
        {!node.modifiedBy ? '—' : node.modifiedBy.toLowerCase() === myEmail.toLowerCase() ? 'you' : node.modifiedBy}
      </td>
      {/* The menu's own clicks must not also open the row. */}
      <td className="pr-2 py-2 w-8" onClick={(e) => e.stopPropagation()}>
        <RowMenu label={`Actions for ${node.name}`} tone="default" items={menu} />
      </td>
    </tr>
  );
}

/**
 * One open folder: where it is, what you can do in it, and what is in it.
 *
 * The whole listing is a drop zone for files and folders from the desktop, and every folder row (and
 * every breadcrumb above the current folder) is a drop zone for rows dragged within the page. The two
 * are told apart by the drag's type, which is all a browser lets a page read before the drop.
 */
export function FolderView({
  folderId, me, state, handlers, dragging, setDragging, uploadIntent, onDismissUploadIntent,
}: {
  folderId: string;
  me: SpaceSummary;
  state: FolderListingState;
  handlers: FolderViewHandlers;
  dragging: SpaceNodeView | null;
  setDragging: (node: SpaceNodeView | null) => void;
  /** Arrived with `?upload=1` — from an MCP client that cannot send the bytes itself. */
  uploadIntent: boolean;
  onDismissUploadIntent: () => void;
}) {
  const { listing, children, nextCursor, loading, loadingMore, error } = state;
  // A file just uploaded reads "indexing" until its text is in; re-read until it is, so the chip settles on its own.
  useRefreshWhileIndexing(children.some((c) => c.indexStatus === 'PENDING'), state.reload, folderId);
  const filesInput = useRef<HTMLInputElement>(null);
  const folderInput = useRef<HTMLInputElement>(null);
  const [fileDragOver, setFileDragOver] = useState(false);
  const [naming, setNaming] = useState<{ kind: 'new' } | { kind: 'rename'; node: SpaceNodeView } | null>(null);
  const [dropRefusal, setDropRefusal] = useState<string | null>(null);

  // `webkitdirectory` is not in React's attribute types, and as a DOM property it is.
  useEffect(() => {
    if (folderInput.current) folderInput.current.webkitdirectory = true;
  }, [listing]);

  const crumbs = listing?.breadcrumbs ?? [];
  const here = crumbs[crumbs.length - 1] ?? null;
  const atSpaceRoot = Boolean(listing?.space.owner && crumbs.length === 1);
  const uploadBlock = !listing
    ? null
    : !me.uploadsEnabled
      ? 'Uploads are not available here: file storage is not configured on this deployment. Folders and sharing still work.'
      : !listing.can.add
        ? 'You can view this folder but not add to it.'
        : null;

  // A deep link asks for the picker on arrival. Browsers only open one in response to a click, so this
  // usually does nothing — which is why the banner below carries its own button — but where it is
  // allowed (a link opened from this page, say) it saves a step.
  const triedPicker = useRef(false);
  useEffect(() => {
    if (!uploadIntent || triedPicker.current || !listing || uploadBlock) return;
    triedPicker.current = true;
    const input = atSpaceRoot ? folderInput.current : filesInput.current;
    try {
      input?.showPicker();
    } catch {
      // No user activation: the banner's button is the way in.
    }
  }, [uploadIntent, listing, uploadBlock, atSpaceRoot]);

  function upload(files: DroppedFile[]) {
    if (files.length === 0) return;
    if (files.length > MAX_FILES_PER_UPLOAD) {
      setDropRefusal(
        `That is more than ${MAX_FILES_PER_UPLOAD.toLocaleString()} files, which is the most one upload can carry. ` +
          'Put them in one archive (.zip, .7z, .tar, .tar.gz or .tar.xz) and upload that instead: it is unpacked into a folder beside it by itself.',
      );
      return;
    }
    setDropRefusal(null);
    if (uploadIntent) onDismissUploadIntent();
    handlers.onUpload(files);
  }

  async function onDrop(e: React.DragEvent) {
    setFileDragOver(false);
    // Prevented whether or not it is accepted: a file dropped on a page that does not prevent it is
    // opened by the browser, replacing the console with the file.
    e.preventDefault();
    if (!isFileDrag(e)) return;
    if (uploadBlock) {
      setDropRefusal(uploadBlock);
      return;
    }
    const pending = readDrop(e.dataTransfer);
    const { files, unreadable } = await pending;
    if (unreadable.length > 0) handlers.onError(`Could not read ${unreadable.slice(0, 3).join(', ')}${unreadable.length > 3 ? ` and ${unreadable.length - 3} more` : ''}`);
    if (files.length === 0) {
      if (unreadable.length === 0) setDropRefusal('That drop held no files. Empty folders are not created by a drop; use New folder.');
      return;
    }
    upload(files);
  }

  async function rename(node: SpaceNodeView, name: string) {
    if (node.type === 'folder') await spacesApi.updateFolder(node.id, { name });
    else await spacesApi.updateFile(node.id, { name });
    handlers.onChanged([node.parentId, node.type === 'folder' ? node.id : null]);
  }

  async function trash(node: SpaceNodeView) {
    const days = me.trashDays;
    const where = listing?.space.owner
      ? `You can restore it from Trash for ${days} days.`
      : `${listing?.space.ownerEmail ?? 'The owner'} can restore it from their trash for ${days} days.`;
    if (!window.confirm(`Move “${node.name}” to the trash?${node.type === 'folder' ? ' Everything inside it goes with it.' : ''} ${where}`)) return;
    try {
      if (node.type === 'folder') await spacesApi.trashFolder(node.id);
      else await spacesApi.trashFile(node.id);
      handlers.onChanged([node.parentId]);
      if (node.id === folderId) {
        const parent = crumbs[crumbs.length - 2];
        if (parent) handlers.onOpenFolder(parent.id);
      }
    } catch (err) {
      handlers.onError(spaceErrorMessage(err, 'Could not move it to the trash'));
    }
  }

  if (!listing) {
    return error ? (
      <div className="rounded-md border border-danger-ring bg-danger-bg px-4 py-3 text-sm text-danger-text">{error}</div>
    ) : (
      <p className="text-xs text-ink-faint">{loading ? 'Loading…' : ''}</p>
    );
  }

  // The open folder as a node, for renaming and trashing it from its own header. The server's own view
  // of it when it sent one; otherwise built from the crumbs, which carry everything those two need.
  const self: SpaceNodeView | null =
    listing.folder ??
    (here
      ? {
          type: 'folder', id: here.id, name: here.name, address: here.address, spaceHandle: listing.space.handle,
          parentId: crumbs[crumbs.length - 2]?.id ?? null, createdById: '', updatedAt: '',
        }
      : null);
  const access = accessOf(listing);
  const title = here ? crumbLabel(here, crumbs.length - 1, listing.space) : 'Folder';
  const address = here?.address ?? listing.folder?.address ?? '';

  return (
    <div>
      {/* Breadcrumbs */}
      <nav className="flex items-center flex-wrap gap-0.5 mb-3 -ml-1.5" aria-label="Folder path">
        {crumbs.map((crumb, index) => (
          <span key={crumb.id} className="flex items-center gap-0.5 min-w-0">
            {index > 0 && <ChevronRight size={11} className="text-ink-faint flex-shrink-0" />}
            <Crumb
              crumb={crumb}
              label={crumbLabel(crumb, index, listing.space)}
              last={index === crumbs.length - 1}
              dragging={dragging}
              onOpen={() => handlers.onOpenFolder(crumb.id)}
              onMove={handlers.onMove}
            />
          </span>
        ))}
      </nav>

      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="min-w-0">
          <h1 className="text-lg font-bold text-ink truncate flex items-center gap-2">
            {title}
            {listing.folder?.shared && <Badge variant="info">Shared</Badge>}
          </h1>
          <p className="mt-1 text-[11px] text-ink-muted flex items-center gap-1.5 flex-wrap">
            <button
              type="button"
              onClick={() => handlers.onCopyPath(address)}
              title="Copy MCP path — tell Claude to save something here"
              className="inline-flex items-center gap-1 font-mono text-ink-secondary hover:text-ink"
            >
              {address}
              <Copy size={10} className="text-ink-faint" />
            </button>
            {access !== 'owner' && (
              <span>
                · shared with you by {listing.space.ownerEmail} · <span className="font-medium">{access}</span>
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {/* A space's root is never shared as a whole — only folders inside it — so no Share at the top. */}
          {listing.can.manage && !atSpaceRoot && (
            <Button size="sm" onClick={() => handlers.onShare({ id: folderId, address })}>
              <Share2 size={12} />
              Share
            </Button>
          )}
          {listing.can.delete && self && (
            <>
              <Button size="sm" variant="ghost" title="Rename this folder" onClick={() => setNaming({ kind: 'rename', node: self })}>
                <Pencil size={12} />
              </Button>
              <Button size="sm" variant="ghost" title="Move this folder to the trash" onClick={() => void trash(self)}>
                <Trash2 size={12} />
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        {listing.can.add && (
          <Button size="sm" onClick={() => setNaming({ kind: 'new' })}>
            <FolderPlus size={12} />
            New folder
          </Button>
        )}
        {listing.can.add && (
          <>
            {!atSpaceRoot && (
              <Button size="sm" variant="primary" disabled={uploadBlock !== null} title={uploadBlock ?? 'Upload files into this folder'} onClick={() => filesInput.current?.click()}>
                <Upload size={12} />
                Upload files
              </Button>
            )}
            <Button
              size="sm"
              variant={atSpaceRoot ? 'primary' : 'secondary'}
              disabled={uploadBlock !== null}
              title={uploadBlock ?? 'Upload a folder and everything in it'}
              onClick={() => folderInput.current?.click()}
            >
              <FolderUp size={12} />
              Upload folder
            </Button>
          </>
        )}
        {atSpaceRoot && listing.can.add && (
          <span className="text-[11px] text-ink-faint">The top of your space holds folders only — open one to upload files into it.</span>
        )}
        {uploadBlock && listing.can.add && <span className="text-[11px] text-ink-faint">{uploadBlock}</span>}
        <input
          ref={filesInput}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => {
            const files = filesFromInput(e.target.files);
            e.target.value = '';
            upload(files);
          }}
        />
        <input
          ref={folderInput}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => {
            const files = filesFromInput(e.target.files);
            e.target.value = '';
            upload(files);
          }}
        />
      </div>

      {uploadIntent && (
        <div className="mb-3 flex items-center gap-3 rounded-md border border-orange-300 dark:border-orange-900 bg-orange-50 dark:bg-orange-950/30 px-4 py-3">
          <UploadCloud size={16} className="text-orange-500 flex-shrink-0" />
          <p className="text-xs text-ink-secondary flex-1">
            {uploadBlock
              ? uploadBlock
              : atSpaceRoot
                ? 'You were sent here to upload. Files go inside a folder: open one, or upload a whole folder here.'
                : <>Choose the files to upload to <span className="font-mono">{address}</span>, or drop them anywhere below.</>}
          </p>
          {!uploadBlock && (
            <Button size="sm" variant="primary" onClick={() => (atSpaceRoot ? folderInput : filesInput).current?.click()}>
              {atSpaceRoot ? 'Choose a folder' : 'Choose files'}
            </Button>
          )}
          <button type="button" onClick={onDismissUploadIntent} className="p-1 rounded text-ink-faint hover:text-ink" aria-label="Dismiss">
            <X size={14} />
          </button>
        </div>
      )}

      {error && <div className="mb-3 rounded-md border border-danger-ring bg-danger-bg px-4 py-2 text-xs text-danger-text">{error}</div>}
      {dropRefusal && (
        <div className="mb-3 flex items-center gap-2 rounded-md border border-warning-ring bg-warning-bg px-4 py-2 text-xs text-warning-text">
          <span className="flex-1">{dropRefusal}</span>
          <button type="button" onClick={() => setDropRefusal(null)} aria-label="Dismiss"><X size={12} /></button>
        </div>
      )}

      {/* The listing, and the drop zone around it */}
      <div
        onDragOver={(e) => {
          if (isNodeDrag(e) || !isFileDrag(e)) return;
          e.preventDefault();
          // 'copy' even when uploads are blocked: a refused drop fires no `drop` event, and the drop is
          // where this says why.
          e.dataTransfer.dropEffect = 'copy';
          setFileDragOver(true);
        }}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFileDragOver(false);
        }}
        onDrop={(e) => void onDrop(e)}
        className={cn(
          'relative rounded-lg border bg-card overflow-hidden transition-colors',
          fileDragOver && !uploadBlock ? 'border-orange-400 border-dashed border-2' : 'border-border',
        )}
      >
        {fileDragOver && !uploadBlock && (
          <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-orange-50/80 dark:bg-orange-950/60">
            <p className="text-sm font-medium text-orange-600 dark:text-orange-400 flex items-center gap-2">
              <UploadCloud size={16} />
              {atSpaceRoot ? 'Drop folders here — files go inside them' : `Drop to upload to ${address}`}
            </p>
          </div>
        )}

        {children.length === 0 ? (
          <div className="py-16 text-center text-ink-faint">
            <Folder size={20} className="mx-auto mb-2 opacity-60" />
            <p className="text-sm max-w-sm mx-auto">
              {loading
                ? 'Loading…'
                : atSpaceRoot
                  ? 'Your space is empty. Create a folder to start — files live inside folders.'
                  : listing.can.add && !uploadBlock
                    ? 'This folder is empty. Drop files or folders here, or use Upload.'
                    : 'This folder is empty.'}
            </p>
          </div>
        ) : (
          <table className="w-full table-fixed">
            <colgroup>
              <col />
              <col className="w-24" />
              <col className="w-28" />
              <col className="w-52" />
              <col className="w-10" />
            </colgroup>
            <thead>
              <tr className="border-b border-border text-left">
                <th className="pl-4 pr-2 py-2 text-[11px] font-semibold text-ink-faint uppercase tracking-wide">Name</th>
                <th className="px-2 py-2 text-[11px] font-semibold text-ink-faint uppercase tracking-wide">Size</th>
                <th className="px-2 py-2 text-[11px] font-semibold text-ink-faint uppercase tracking-wide">Modified</th>
                <th className="px-2 py-2 text-[11px] font-semibold text-ink-faint uppercase tracking-wide">Modified by</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {children.map((node) => (
                <ListingRow
                  key={node.id}
                  node={node}
                  canEdit={listing.can.edit}
                  isOwner={listing.space.owner}
                  myEmail={me.ownerEmail}
                  dragging={dragging}
                  setDragging={setDragging}
                  handlers={handlers}
                  onRename={(n) => setNaming({ kind: 'rename', node: n })}
                  onTrash={(n) => void trash(n)}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {nextCursor && (
        <div className="mt-3 flex justify-center">
          <Button size="sm" onClick={state.loadMore} disabled={loadingMore}>
            {loadingMore && <Loader2 size={12} className="animate-spin" />}
            Load more
          </Button>
        </div>
      )}

      <NameDialog
        open={naming !== null}
        title={naming?.kind === 'rename' ? `Rename ${naming.node.type}` : 'New folder'}
        label="Name"
        initial={naming?.kind === 'rename' ? naming.node.name : ''}
        submitLabel={naming?.kind === 'rename' ? 'Rename' : 'Create'}
        onClose={() => setNaming(null)}
        onSubmit={async (name) => {
          if (naming?.kind === 'rename') await rename(naming.node, name);
          else {
            await spacesApi.createFolder({ parentId: folderId, name });
            handlers.onChanged([folderId]);
          }
        }}
      />
    </div>
  );
}
